import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import CurrencyInput from '../shared/CurrencyInput';
import TrustFilieBadge from '../shared/TrustFilieBadge';
import {
  updateDeductionDetails,
  markFieldOverride,
} from '../../store/questionnaireSlice';

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

type DeductionPreference = 'standard' | 'itemized' | 'unsure';

interface TrustFilieToggleProps {
  enabled: boolean;
  onToggle: () => void;
}

function TrustFilieToggle({ enabled, onToggle }: TrustFilieToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
        enabled ? 'bg-indigo-100' : 'bg-gray-200'
      }`}
      title={enabled ? 'Filie will check this for you' : 'Enter manually'}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
          enabled ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

interface AdjustmentFieldProps {
  label: string;
  fieldKey: string;
  value: number;
  onChange: (value: number) => void;
  trustFilieDefault: boolean;
  preFilledValue?: number | null;
  preFilledSource?: 'document' | null;
  infoText?: string;
}

function AdjustmentField({
  label,
  fieldKey: _fieldKey,
  value,
  onChange,
  trustFilieDefault,
  preFilledValue,
  preFilledSource,
  infoText,
}: AdjustmentFieldProps) {
  const isPreFilled = preFilledValue !== null && preFilledValue !== undefined;
  const [trustFilie, setTrustFilie] = useState(
    isPreFilled ? false : trustFilieDefault
  );

  // If there's an info-only display (like employer retirement contributions)
  if (infoText) {
    return (
      <div className="flex items-center justify-between py-3">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-700">{label}</p>
          <p className="text-xs text-gray-500 mt-0.5">{infoText}</p>
        </div>
        {preFilledSource === 'document' && (
          <TrustFilieBadge source="document" documentType="W-2" />
        )}
      </div>
    );
  }

  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <div className="flex items-center space-x-2">
          {isPreFilled && <TrustFilieBadge source="document" />}
          {!isPreFilled && trustFilie && (
            <span className="text-xs text-gray-400">Filie will check</span>
          )}
          {!isPreFilled && (
            <TrustFilieToggle
              enabled={trustFilie}
              onToggle={() => setTrustFilie(!trustFilie)}
            />
          )}
        </div>
      </div>
      {trustFilie && !isPreFilled ? (
        <div className="px-3 py-2 border border-gray-100 rounded-lg bg-gray-50 text-sm text-gray-400">
          Filie will determine the best value
        </div>
      ) : (
        <CurrencyInput
          value={isPreFilled && value === 0 ? preFilledValue : value}
          onChange={onChange}
        />
      )}
    </div>
  );
}

export default function DeductionsAdjustmentsStep() {
  const dispatch = useDispatch();
  const { answers, smartDefaults } = useSelector(
    (state: RootState) => state.questionnaire
  );

  const details = answers.deductionDetails;
  const preference: DeductionPreference = (details?.preference as DeductionPreference) || 'unsure';
  const itemized = details?.itemized || {
    mortgageInterest: 0,
    stateLocalTaxes: 0,
    charitableContributions: 0,
    medicalExpenses: 0,
  };
  const adjustments = details?.adjustments || {
    studentLoanInterest: 0,
    educatorExpenses: 0,
    hsaContributions: 0,
    hsaType: 'individual' as const,
    traditionalIra: 0,
    seHealthInsurance: 0,
  };

  const handlePreferenceChange = (value: DeductionPreference) => {
    dispatch(updateDeductionDetails({ preference: value }));
    dispatch(markFieldOverride('deductionPreference'));
  };

  const handleItemizedChange = (
    field: keyof typeof itemized,
    value: number
  ) => {
    dispatch(
      updateDeductionDetails({ itemized: { ...itemized, [field]: value } })
    );
    dispatch(markFieldOverride(`itemized_${field}`));
  };

  const handleAdjustmentChange = (
    field: keyof typeof adjustments,
    value: number
  ) => {
    dispatch(
      updateDeductionDetails({
        adjustments: { ...adjustments, [field]: value },
      })
    );
    dispatch(markFieldOverride(`adjustment_${field}`));
  };

  const preferenceOptions: Array<{
    value: DeductionPreference;
    label: string;
    desc: string;
  }> = [
    {
      value: 'standard',
      label: 'Standard Deduction',
      desc: 'Fixed amount based on filing status',
    },
    {
      value: 'itemized',
      label: 'Itemized Deductions',
      desc: 'List individual deductions if they exceed the standard amount',
    },
    {
      value: 'unsure',
      label: 'Trust Filie (will recommend)',
      desc: "We'll analyze your data and recommend the best option",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Deductions & Adjustments
        </h2>
        <p className="text-gray-500 mt-1">
          Tell us about deductions or let Filie figure it out
        </p>
      </div>

      {/* Deduction Preference */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">
          Deduction Preference
        </p>
        {preferenceOptions.map((opt) => (
          <label
            key={opt.value}
            className="flex items-start p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <input
              type="radio"
              name="deductionPreference"
              value={opt.value}
              checked={preference === opt.value}
              onChange={() => handlePreferenceChange(opt.value)}
              className="mt-0.5 w-4 h-4 text-gray-600 cursor-pointer"
            />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">{opt.label}</p>
              <p className="text-xs text-gray-500">{opt.desc}</p>
            </div>
          </label>
        ))}
      </div>

      {/* Itemized Deductions (conditional) */}
      {preference === 'itemized' && (
        <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-2">
            Itemized Deductions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
            <div className="py-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mortgage Interest
              </label>
              <CurrencyInput
                value={itemized.mortgageInterest}
                onChange={(v) => handleItemizedChange('mortgageInterest', v)}
              />
            </div>
            <div className="py-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State & Local Taxes (SALT)
              </label>
              <CurrencyInput
                value={itemized.stateLocalTaxes}
                onChange={(v) => handleItemizedChange('stateLocalTaxes', v)}
              />
            </div>
            <div className="py-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Charitable Contributions
              </label>
              <CurrencyInput
                value={itemized.charitableContributions}
                onChange={(v) =>
                  handleItemizedChange('charitableContributions', v)
                }
              />
            </div>
            <div className="py-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical Expenses
              </label>
              <CurrencyInput
                value={itemized.medicalExpenses}
                onChange={(v) => handleItemizedChange('medicalExpenses', v)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Above-the-Line Adjustments */}
      <div className="border border-gray-200 rounded-lg p-5 bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-1">
          Above-the-Line Adjustments
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          These reduce your AGI regardless of whether you itemize
        </p>

        <div className="divide-y divide-gray-100">
          {/* HSA */}
          <AdjustmentField
            label="HSA Contributions"
            fieldKey="hsaContributions"
            value={adjustments.hsaContributions}
            onChange={(v) => handleAdjustmentChange('hsaContributions', v)}
            trustFilieDefault={true}
            preFilledValue={smartDefaults?.hsaContributions}
            preFilledSource={
              smartDefaults?.hsaContributions ? 'document' : null
            }
          />

          {/* Retirement Contributions (info-only if from W-2) */}
          {smartDefaults?.retirementContributions ? (
            <AdjustmentField
              label="Retirement Contributions"
              fieldKey="retirementContributions"
              value={0}
              onChange={() => {}}
              trustFilieDefault={false}
              preFilledValue={smartDefaults.retirementContributions}
              preFilledSource="document"
              infoText={`Your employer contributed ${formatCurrency(smartDefaults.retirementContributions)} (from W-2)`}
            />
          ) : null}

          {/* Traditional IRA */}
          <AdjustmentField
            label="Traditional IRA Contributions"
            fieldKey="traditionalIra"
            value={adjustments.traditionalIra}
            onChange={(v) => handleAdjustmentChange('traditionalIra', v)}
            trustFilieDefault={true}
          />

          {/* Student Loan Interest */}
          <AdjustmentField
            label="Student Loan Interest"
            fieldKey="studentLoanInterest"
            value={adjustments.studentLoanInterest}
            onChange={(v) => handleAdjustmentChange('studentLoanInterest', v)}
            trustFilieDefault={true}
          />

          {/* Educator Expenses */}
          <AdjustmentField
            label="Educator Expenses"
            fieldKey="educatorExpenses"
            value={adjustments.educatorExpenses}
            onChange={(v) => handleAdjustmentChange('educatorExpenses', v)}
            trustFilieDefault={true}
          />

          {/* SE Health Insurance */}
          <AdjustmentField
            label="Self-Employed Health Insurance"
            fieldKey="seHealthInsurance"
            value={adjustments.seHealthInsurance}
            onChange={(v) => handleAdjustmentChange('seHealthInsurance', v)}
            trustFilieDefault={true}
          />
        </div>
      </div>
    </div>
  );
}
